import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import { allowDecimalNumbers } from "../../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { CurrencyConversionToWords } from "../commonComponents";
import {
    GetBrokerBankDetailsResponseBody, GetNonMfBrokersListResponseBody
} from "../../../../redux-store/types/orderManagement";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import { getBrokerBankDetails } from "../../../../redux-store/actions/orderManagement";
import {
    Dispatch,
    SetStateAction
} from "react";

export default function MarginMoneyForm({
    nonMfBrokersList,
    fetchNonMfBrokers,
    isLoading,
    setLoadingStatus
}: {
    nonMfBrokersList: GetNonMfBrokersListResponseBody;
    fetchNonMfBrokers: (searchInput: string) => void;
    isLoading: boolean;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
}): JSX.Element {
    const dispatch = useDispatch();

    const { currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleOrderDateChange = (selectedDate: any) => {
        setFieldValue('orderDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleAmountPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = `${clipboardData ?? ""}`?.replaceAll(",", "")

        setFieldValue("amount", modifiedText);
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                settlementMethod: ""
            }
        })
    }

    const handleBrokerChange = async (selectedObj: Record<string, any>) => {
        try {
            setLoadingStatus("apiLoading")

            await setFieldValue("broker", selectedObj)

            const payload = {
                clientCode: values?.clientName?.clientCode ?? ""
            }

            let response: GetBrokerBankDetailsResponseBody | null = null

            if (selectedObj) {
                response = await (dispatch(getBrokerBankDetails(payload)) as unknown as Promise<GetBrokerBankDetailsResponseBody>);
            }

            resetForm({
                values: {
                    ...values,
                    broker: selectedObj,
                    brokerBankDetails: response,
                    settlementBankNumber: selectedObj
                        ? {
                            ...(response ?? {}),
                            label: response?.bankAccNo ?? "",
                            value: response?.bankAccNo ?? ""
                        }
                        : null,
                    settlementBank: selectedObj ? response?.bankName ?? "" : "",
                    settlementBankIfsc: selectedObj ? response?.ifscCode ?? "" : ""
                }
            })
        } catch (error) {
            console.log(error ?? "")
        } finally {
            setLoadingStatus("")
        }
    }

    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')

    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleOrderDateChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Margin Money" },
                        // { name: "Cash In Broker" },
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    onChange={handleTransactionTypeChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="amount"
                    label="Amount *"
                    placeholder=""
                    startAdornment={values?.amount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: isLoading ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    onPaste={handleAmountPaste}
                    value={values?.amount ?? ""}
                    disabled={isLoading}
                />
                <CurrencyConversionToWords value={refactoredAmount} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="broker"
                    label="Broker *"
                    options={nonMfBrokersList?.brokers?.map(obj => ({
                        ...obj,
                        label: obj?.description ?? "",
                        value: obj?.identifier ?? ""
                    })) ?? []}
                    fetchData={fetchNonMfBrokers}
                    filterOptions={(x) => x}
                    onChange={handleBrokerChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}